/* eslint-disable @typescript-eslint/no-explicit-any */

import { isAxiosError } from 'axios';

import { apiAdmin, ApiResponse } from '@/api/api';
import { ADMIN_URLS } from '@/api/url';
import { LoginResponse } from './auth.service';

import { AccountRole, AgencyPlan } from '@/types/enums';
import { AgencyFeature } from '@/types/enums/AgencyFeature';
import {
  AlignedSkill,
  AlertsAndFollowUpQuestion,
  GapsOrMisalignment,
  InterviewContent,
  Score
} from '@/types/AnalysisResult';
import { JobRequirements } from '@/types/Job';
import { JobDescription } from '@/types/JobDescription';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { LlmModelProvider } from '@/types/enums/LlmModelProvider';
import { PromptKey } from '@/types/enums/PromptKey';
import { Agency } from '@/types/Agency';
import { MilitaryTranslationSample } from '@/types/military_translation_sample';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  admin: Admin;
  access_token: string;
  access_token_expired_at: string;
  refresh_token: string;
  refresh_token_expired_at: string;
}

export const apiAdminLogin = async (
  payload: LoginPayload
): Promise<ApiResponse<AdminLoginResponse>> => {
  try {
    const res = await apiAdmin.post<ApiResponse<AdminLoginResponse>>(ADMIN_URLS.LOGIN, payload);
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetAdminProfile = async (): Promise<ApiResponse<Admin>> => {
  try {
    const res = await apiAdmin.get<ApiResponse<Admin>>(ADMIN_URLS.GET_PROFILE);
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiGetOpenaiAssistant = async (name: string): Promise<ApiResponse<Assistant>> => {
  try {
    const res = await apiAdmin.get<ApiResponse<Assistant>>(ADMIN_URLS.OPENAI.GET_ASSISTANT(name));
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateOpenaiAssistantPayload {
  instructions: string;
}

export const apiUpdateOpenaiAssistant = async (
  name: string,
  payload: UpdateOpenaiAssistantPayload
): Promise<ApiResponse<Assistant>> => {
  try {
    const res = await apiAdmin.put<ApiResponse<Assistant>>(
      ADMIN_URLS.OPENAI.UPDATE_ASSISTANT(name),
      payload
    );
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminExtractJDPayload {
  file: File;
}

export const apiAdminExtractJD = async ({
  payload
}: {
  payload: AdminExtractJDPayload;
}): Promise<ApiResponse<JobDescription>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await apiAdmin.post<ApiResponse<JobDescription>>(
      ADMIN_URLS.EXTRACTOR.EXTRACT_JD,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminExtractResumePayload {
  file: File;
}

export const apiAdminExtractResume = async ({
  payload
}: {
  payload: AdminExtractResumePayload;
}): Promise<ApiResponse<Resume>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await apiAdmin.post<ApiResponse<Resume>>(
      ADMIN_URLS.EXTRACTOR.EXTRACT_RESUME,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminExtractVeteransResumePayload {
  file: File;
}

export interface AdminExtractVeteransResumeResponse {
  content: string;
  military_types: string[];
}

export const apiAdminExtractVeteransResume = async ({
  payload
}: {
  payload: AdminExtractVeteransResumePayload;
}): Promise<ApiResponse<AdminExtractVeteransResumeResponse>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await apiAdmin.post<ApiResponse<AdminExtractVeteransResumeResponse>>(
      ADMIN_URLS.EXTRACTOR.EXTRACT_VETERANS_RESUME,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminGenerateVeteransResumePayload {
  file: File;
}

export interface VeteransResumeGenerationProgress {
  percentage: number;
  step: string;
}

export const apiAdminGenerateVeteransResume = async ({
  payload,
  onProgress
}: {
  payload: AdminGenerateVeteransResumePayload;
  onProgress?: (progress: VeteransResumeGenerationProgress) => void;
}): Promise<ApiResponse<Resume>> => {
  const { default: useAdminAuth } = await import('@/store/adminAuth');
  const { accessToken } = useAdminAuth.getState();

  const formData = new FormData();
  formData.append('file', payload.file);

  const response = await fetch(ADMIN_URLS.GENERATOR.GENERATE_VETERANS_RESUME, {
    method: 'POST',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    },
    body: formData
  });

  if (!response.ok || !response.body) {
    const errorData = await response
      .json()
      .catch(() => ({ code: response.status, message: response.statusText, data: null }));
    return errorData;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let finalData: ApiResponse<Resume> | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (currentEvent === 'done') {
            finalData = parsed as ApiResponse<Resume>;
          } else {
            onProgress?.(parsed as VeteransResumeGenerationProgress);
          }
        } catch {
          console.error('Failed to parse line:', line);
        }
        currentEvent = '';
      } else if (line === '') {
        currentEvent = '';
      }
    }
  }

  if (!finalData) {
    return { code: 500, message: 'No response data received', data: null as any };
  }

  return finalData;
};

export const apiAdminGetPrompt = async ({
  key
}: {
  key: PromptKey;
}): Promise<ApiResponse<Prompt>> => {
  try {
    const response = await apiAdmin.get<ApiResponse<Prompt>>(ADMIN_URLS.PROMPT.GET(key));

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

interface UpdatePromptPayload {
  prompt_text: string;
}

export const apiAdminUpdatePrompt = async ({
  key,
  payload
}: {
  key: string;
  payload: UpdatePromptPayload;
}): Promise<ApiResponse<Prompt>> => {
  try {
    const response = await apiAdmin.put<ApiResponse<Prompt>>(
      ADMIN_URLS.PROMPT.UPDATE(key),
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

interface AnalyzeJobRequirementsPayload {
  job_title: string;
  job_description: string;
}

export interface AnalyzeJobRequirementsResult {
  requirements: string[];
  nice_to_haves: string[];
  position_tags: string[];
}

export const apiAdminAnalyzeJobRequirements = async ({
  payload
}: {
  payload: AnalyzeJobRequirementsPayload;
}): Promise<ApiResponse<AnalyzeJobRequirementsResult>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<AnalyzeJobRequirementsResult>>(
      ADMIN_URLS.ANALYZER.JOB_REQUIREMENTS,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AnalyzeResumePayload {
  job_title: string;
  job_description: string;
  job_requirements: JobRequirements;
  resume: Resume;
}

export interface AnalyzeResumeResponse {
  overview: string;
  aligned_skills: AlignedSkill[];
  gaps_or_misalignments: GapsOrMisalignment[];
  fit_score: Score;
  alerts_and_follow_up_questions: AlertsAndFollowUpQuestion[];
}

export const apiAdminAnalyzeResume = async ({
  payload
}: {
  payload: AnalyzeResumePayload;
}): Promise<ApiResponse<AnalyzeResumeResponse>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<AnalyzeResumeResponse>>(
      ADMIN_URLS.ANALYZER.RESUME,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AnalyzeInterviewPayload {
  job_title: string;
  job_description: string;
  job_requirements: JobRequirements;
  alerts_and_follow_up_questions: AlertsAndFollowUpQuestion[];
  aligned_skills: AlignedSkill[];
  gaps_or_misalignments: GapsOrMisalignment[];
  transcripts: Transcript[];
}

export interface Transcript {
  speaker_name: string;
  text: string;
}

export interface AnalyzeInterviewResponse {
  overview: string;
  aligned_skills: AlignedSkill[];
  gaps_or_misalignments: GapsOrMisalignment[];
  score: Score;
  contents: InterviewContent[];
  alerts_and_follow_up_questions: AlertsAndFollowUpQuestion[];
}

export const apiAdminAnalyzeInterview = async ({
  payload
}: {
  payload: AnalyzeInterviewPayload;
}): Promise<ApiResponse<AnalyzeInterviewResponse>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<AnalyzeInterviewResponse>>(
      ADMIN_URLS.ANALYZER.INTERVIEW,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface Recipient {
  name: string;
  email: string;
}

export interface SendBatchEmailPayload {
  recipients: Recipient[];
  subject: string;
  body: string;
}

export const apiAdminSendBatchEmail = async ({
  payload
}: {
  payload: SendBatchEmailPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<void>>(
      ADMIN_URLS.MAILER.SEND_BATCH_EMAIL,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AnalyzeOverviewPayload {
  resume_overview: string;
  interview_overview: string;
}

export interface AnalyzeOverviewResponse {
  overview: string;
}

export const apiAdminAnalyzeOverview = async ({
  payload
}: {
  payload: AnalyzeOverviewPayload;
}): Promise<ApiResponse<AnalyzeOverviewResponse>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<AnalyzeOverviewResponse>>(
      ADMIN_URLS.ANALYZER.OVERVIEW,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface CreateUserByAdminPayload {
  email: string;
  password: string;
  name: string;

  agency_name?: string;
  plan?: AgencyPlan;

  agency_id?: string;
  role?: AccountRole;
}

export const apiAdminCreateUserByAdmin = async ({
  payload
}: {
  payload: CreateUserByAdminPayload;
}): Promise<ApiResponse<LoginResponse>> => {
  try {
    const response = await apiAdmin.post<ApiResponse<LoginResponse>>(
      ADMIN_URLS.USER.CREATE,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetUsersParams {
  page?: number;
  limit?: number;
  keyword?: string;
}

export interface GetUsersResponse {
  accounts: Account[];
  current_page: number;
  total_page: number;
}

export const apiAdminGetUsers = async (
  params: GetUsersParams
): Promise<ApiResponse<GetUsersResponse>> => {
  try {
    const res = await apiAdmin.get<ApiResponse<GetUsersResponse>>(ADMIN_URLS.USER.GET_ALL, {
      params
    });
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiAdminGetUserProfile = async (accountId: string): Promise<ApiResponse<Account>> => {
  try {
    const res = await apiAdmin.get<ApiResponse<Account>>(ADMIN_URLS.USER.GET(accountId));
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface UpdateAgencyPlanPayload {
  agency_id: string;
  plan: AgencyPlan;
}

export const apiAdminUpdateAgencyPlan = async (
  payload: UpdateAgencyPlanPayload
): Promise<ApiResponse<void>> => {
  try {
    const res = await apiAdmin.put<ApiResponse<void>>(ADMIN_URLS.USER.UPDATE_AGENCY_PLAN, payload);
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiAdminGetAnalysisProviders = async (): Promise<ApiResponse<AnalysisProvider[]>> => {
  try {
    const response = await apiAdmin.get<ApiResponse<AnalysisProvider[]>>(
      ADMIN_URLS.ANALYSIS_PROVIDER.GET
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

interface UpdateAnalysisProviderPayload {
  analysis_name: PromptKey;
  provider: LlmModelProvider;
}

export const apiAdminUpdateAnalysisProvider = async ({
  payload
}: {
  payload: UpdateAnalysisProviderPayload;
}): Promise<ApiResponse<void>> => {
  try {
    const response = await apiAdmin.put<ApiResponse<void>>(
      ADMIN_URLS.ANALYSIS_PROVIDER.UPDATE,
      payload
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminExtractQuestionBankPayload {
  file: File;
}

export const apiAdminExtractQuestionBank = async ({
  payload
}: {
  payload: AdminExtractQuestionBankPayload;
}): Promise<ApiResponse<QuestionBankExtractionData>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await apiAdmin.post<ApiResponse<QuestionBankExtractionData>>(
      ADMIN_URLS.EXTRACTOR.EXTRACT_QUESTION_BANK,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiAdminGetAgencies = async (): Promise<ApiResponse<Agency[]>> => {
  try {
    const res = await apiAdmin.get<ApiResponse<Agency[]>>(ADMIN_URLS.AGENCY.GET_ALL);
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminGetAccountTokenPayload {
  account_id: string;
}

export interface AdminGetAccountTokenResponse {
  account: Account;
  access_token: string;
  access_token_expired_at: string;
  refresh_token: string;
  refresh_token_expired_at: string;
}

export interface UpdateAgencyCustomFeaturePayload {
  agency_id: string;
  feature_key: AgencyFeature;
  is_enabled: boolean;
}

export const apiAdminUpdateAgencyCustomFeature = async (
  payload: UpdateAgencyCustomFeaturePayload
): Promise<ApiResponse<void>> => {
  try {
    const res = await apiAdmin.put<ApiResponse<void>>(
      ADMIN_URLS.AGENCY.UPDATE_CUSTOM_FEATURE,
      payload
    );
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiAdminGetAccountToken = async (
  payload: AdminGetAccountTokenPayload
): Promise<ApiResponse<AdminGetAccountTokenResponse>> => {
  try {
    const res = await apiAdmin.post<ApiResponse<AdminGetAccountTokenResponse>>(
      ADMIN_URLS.USER.GENERATE_TOKEN,
      payload
    );
    return res.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminTestEmbeddingPayload {
  file: File;
}

export interface AdminTestEmbeddingResponse {
  provider: LlmModelProvider;
  vector: number[];
}

export const apiAdminTestEmbedding = async ({
  payload
}: {
  payload: AdminExtractResumePayload;
}): Promise<ApiResponse<AdminTestEmbeddingResponse>> => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await apiAdmin.post<ApiResponse<AdminTestEmbeddingResponse>>(
      ADMIN_URLS.EMBEDDING.TEST,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface AdminCreateMilitaryTranslationSamplePayload {
  military_file: File;
  corporate_file: File;
}

export const apiAdminCreateMilitaryTranslationSample = async ({
  payload
}: {
  payload: AdminCreateMilitaryTranslationSamplePayload;
}): Promise<ApiResponse<MilitaryTranslationSample>> => {
  try {
    const formData = new FormData();
    formData.append('military_file', payload.military_file);
    formData.append('corporate_file', payload.corporate_file);

    const response = await apiAdmin.post<ApiResponse<MilitaryTranslationSample>>(
      ADMIN_URLS.EMBEDDING.MILITARY_TRANSLATION_SAMPLE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
